define((require) => {
    "use strict";

    const _                 = require("underscore");
    const $                 = require("jquery");
    const Marionette        = require("marionette");
    const Tooltip           = require("ui/common/Tooltip");
    const KeyCode           = require("shared/services/KeyCode");
    const Permissions       = require("shared/security/Permissions");
    const Promise           = require("shared/util/Promise");
    const SyncManager       = require("shared/services/sync/SyncManager");
    const Preconditions     = require("shared/util/Preconditions");
    const EditBehaviorUtil  = require("ui/behaviors/EditBehaviorUtil");

    const iconTemplate      = _.template("<div class='editable-cell-img'><div class='editable-img'></div></div>");

    let isFieldBeingEdited  = false;
    let lastSelectedElement = null;

    /**
     * A behavior for the edit fields. The goal of this class is to be used
     * in all places where we need a renaming feature.
     *
     * @class EditBehavior
     * @author Pedro Augusto
     */
    return Marionette.Behavior.extend({

        _model: null,

        fields: [],

        defaults: {
            editFields: [],
            container: null
        },

        /**
         *  Its Create edit object with the parameters needed. With those parameters
         * will be possible have more than one editable field on the view.
         * Even for the views that are used inside of the CollectionView will be
         * possible to use this BehaviorClass.
         *
         * @param {Object}   input           jquery element
         * @param {Object}   icon            jquery element
         * @param {Array}    hideOnFocus     An array of objects with the elements that will be also hidden on focus.
         * @param {String}   model           String with the model associated name.
         * @param {Function} save            callback save function
         * @param {Function} cancel          callback cancel function
         * @param {Boolean}  tooltip         True to enable tooltip, false otherwise.
         * @param {Boolean}  hideIcon        True to hide the image icon, false otherwise.
         *
         * @returns {Object} Returns a new Field object.
         */
        createFieldObject(input, icon, hideOnFocus, model, save, cancel, tooltip, hideIcon) {
            Preconditions.is$element(input);

            const field = {
                icon,
                input,
                hideOnFocus,
                model,
                save,
                cancel,
                tooltip,
                hideIcon,
                originalValue: input.is("input") ? input.val() : input.text(),

                /**
                 * Set or Get the value of an `input` field or a `contenteditable` `div`.
                 *
                 * @param  {String|number} [value] The value to set, if any.
                 * @return {String} Field value.
                 */
                val(value) {
                    if (this.input.is("input")) {
                        if (!_.isUndefined(value)) {
                            this.input.val(value);
                        }

                        return this.input.val();
                    }

                    // It should always set the value as html once that
                    // stickit will have already transformed it.
                    if (!_.isUndefined(value)) {
                        return _.escape(value);
                    }
                    // But, In case of retrieve, we should get a text.
                    return EditBehaviorUtil.sanitize(this.input.text());
                }
            };

            return field;
        },

        /**
         *  Add the icon for the field object.
         *
         * @param {Object} field - Field object
         * @private
         */
        _addImageIcon(field) {
            if (!field.icon && !field.hideIcon) {
                field.icon = $(iconTemplate()).insertAfter(field.input);
            }
        },

        /**
         * Get the container for mouseover and leave effects.
         * It can be passed by parameter on the behavior options
         *
         * @returns {Jquery} Returns the container responsible to activate the hover effect
         */
        getViewContainer() {
            if (!this.options.container) {
                return this.view.$el;
            }

            return this.view.$el.find(this.options.container);
        },

        initialize() {
            // bind all event functions
            _.bindAll(this, "iconMouseEnterHandler", "iconMouseLeaveHandler", "iconStartHandler", "iconEndHandler", "editStart", "editEnd", "editKeyDownHandler");
        },

        /**
         * Bind the events mouse events for the view in question.
         */
        _bindEventsView() {
            this.getViewContainer().on("mouseenter", this.iconMouseEnterHandler);
            this.getViewContainer().on("mouseleave", this.iconMouseLeaveHandler);
        },

        /**
         * Show the icons when the user pass the mouse in the container.
         */
        iconMouseEnterHandler() {
            if (!isFieldBeingEdited) {
                this.showIcons(true);
            }
        },

        /**
         * Hide the icons when the user pass the mouse in the container.
         */
        iconMouseLeaveHandler() {
            this.showIcons(false);
        },

        /**
         * Show or hidden icons of the view
         * @param {boolean} show - It should be showed
         */
        showIcons(show) {
            _.each(this.fields, (field) => {
                if (!field.hideIcon) {
                    field.icon.toggleClass("active", show);
                }
            });
        },

        /**
         * Binds the events for the input and the icon in question.
         * Each input is associated with a icon and they must to have the interactions described on MOS-1698
         *
         * @param {Object}   field   field object
         */
        _bindEventsField(field) {
            if (!field.hideIcon) {
                field.icon.on("mouseup", {field: field}, this.iconStartHandler);
            }

            this.getViewContainer().on("mousedown", {field: field}, this.iconStartHandler);

            field.input.on("blur", {field: field}, this.iconEndHandler);

            field.input.on("keydown", {field: field}, this.editKeyDownHandler);

            if (!field.input.is("input")) {
                field.input.on("paste", {field: field}, this.pasteHandler);
            }
        },
        /**
         * Handler the paste event.
         * If the user trys to paste html it will accept only plain/text.
         *
         * @param {Event} e Jquery paste event.
         */
        pasteHandler(e) {
            let content;

            e.preventDefault();

            if (e.originalEvent.clipboardData) {
                content = (e.originalEvent || e).clipboardData.getData("text/plain");

                document.execCommand("insertText", false, content);
            } else if (window.clipboardData) {
                content = window.clipboardData.getData("Text");

                if (window.getSelection) {
                    window.getSelection().getRangeAt(0).insertNode(document.createTextNode(content));
                }
            }
        },
        /**
         * Remove the bind events from the fields
         */
        onClose() {
            this.getViewContainer().off("mouseenter", "mouseleave", "mousedown");

            _.each(this.fields, (field) => {
                if (!field.hideIcon) {
                    field.icon.off("mouseup");
                }

                field.input.off("blur", "keydown", "paste");
            });
        },

        /**
         *  Handlers the enter and esc input
         *
         * @param {event}   event       KeyDown event
         */
        editKeyDownHandler(event) {
            const field = event.data.field;

            switch (event.which) {
                case KeyCode.ENTER:
                case KeyCode.RETURN:
                    this.iconEndHandler(event);
                    break;
                case KeyCode.ESCAPE:
                    this.editRollback(field);
                    break;
                default: // No log needed here.
                    break;
            }
        },

        /**
         * The user starts to edit the field.
         *
         * @param {Object}  field object
         */
        editStart(field) {
            // Remove the overflow
            field.input.css("text-overflow", "clip");

            this.showIcons(false);

            this.showAdjacentElements(false);

            Tooltip.remove(field.input);
        },
        /**
         * Handler the the start edit event and call the editStart method.
         *
         * @param {Event} event blur.
         */
        iconStartHandler(event) {
            const field = event.data.field;

            this.editStart(field);

            // By feature definition, if you click in the INPUT element the caret position should be positioned in the end.
            // However, if the user click for a second time in the field the caret will keep the wanted position.
            if (lastSelectedElement !== event.target) {
                EditBehaviorUtil.setCaretEndPosition(field.input);

                lastSelectedElement = event.target;
            }

            isFieldBeingEdited = true;

            event.stopPropagation();
        },

        /**
         * Handler the end event and call the editEnd method. Also, show the adjacents elements again like
         * in the case of the imageview that we must to control the timesused tooltip.
         *
         * @param {Event} event Blur.
         */
        iconEndHandler(event) {
            const field = event.data.field;

            this.editEnd(field);

            this.showAdjacentElements(true);

            isFieldBeingEdited = false;

            lastSelectedElement = document.activeElement;

            this._forceFocusMoveOut();

            event.preventDefault();
        },

        /**
         * Manipulate the end of the edition. It must to fire a event for the view passing the new renamed value.
         * However, if the new value is invalid for some reason, It must to Rollback the changes.
         *
         * @param {Object}  field object
         */
        editEnd(field) {
            const newValue = field.val();

            let originalValue = field.originalValue;

            // Update the model in any case to avoid problems with StickIt
            if (!_.isUndefined(field.model)) {
                this._updateModel(field.model, newValue);
            }

            if (newValue !== originalValue && _.isFunction(field.save)) {
                try {
                    const promise = field.save.call(this.view, newValue);

                    // Make sure we check the returned promise for async failure so we can rollback if needed.
                    // We have to supply the actual original value, since it will have been changed above to
                    // prematurely...
                    if (Promise.isPromise(promise)) {
                        promise.fail(() => {
                            this.editRollback(field, originalValue);
                        });
                    }
                } catch (e) {
                    this.editRollback(field);
                }
            }

            field.originalValue = newValue;

            field.input.css("text-overflow", "ellipsis");

            if (field.input.is("div")) {
                field.input.scrollLeft(0);
            }

            if (field.tooltip) {
                this.updateTooltip(field);
            }
        },

        /**
         * Rollback the edited value with the original value passed.
         *
         * @param {Object}  field object
         * @param {Object} [originalValue] The original value to restore in case of async error.
         */
        editRollback(field, originalValue) {
            Preconditions.checkArguments(field);

            if (arguments.length === 1) {
                originalValue = field.originalValue;
            }

            if (_.isFunction(field.cancel)) {
                field.cancel(field.val(), originalValue);
            }

            this._forceFocusMoveOut();

            field.val(originalValue);
        },
        /**
         * Force focus to be moved out of the input field. When the user press enter, escape or blur the field.
         * We should assure that the focus will be removed from the current element.
         *
         * @private
         */
        _forceFocusMoveOut() {
            // Force the focus to be moved out
            EditBehaviorUtil.setCaretEndPosition(this.getViewContainer());
        },

        /**
         * Show or hide the adjacent elements and the icon
         *
         * @param {boolean} show - Should show the adjacent elements.
         *
         */
        showAdjacentElements(show) {
            _.each(this.fields, (field) => {
                if (_.isArray(field.hideOnFocus)) {
                    const arrayElements = this.view.$el.find(field.hideOnFocus.join(","));

                    arrayElements.toggleClass("editable-adjacent-hidden", !show);

                    arrayElements.toggleClass("editable-adjacent-show", show);

                    _.each(arrayElements, (element) => {
                        if (!show) {
                            Tooltip.disable(element);
                        } else {
                            Tooltip.enable(element);
                        }
                    });
                }
            });
        },

        /**
         *   Update the tooltip with the CURRENT field object value
         *   @param {Object}  field object
         */
        updateTooltip(field) {
            if (EditBehaviorUtil.isEllipsisActive(field)) {
                Tooltip.remove(field.input);

                Tooltip.add(field.input, {
                    content: field.val(),
                    placement: "top",
                    alignCenter: true,
                    offset: {
                        align: "center"
                    },
                    width: "auto",
                    delay: {show: 0, hide: 0}
                });
            } else {
                Tooltip.remove(field.input);
            }
        },
        /**
         * @private
         * Update the model with new value. It should be  used to avoid conflicts between the
         * EditBehavior and the StickIt.
         *
         * @param {String} modelName name of the model attribute
         * @param {String} val newValue
         */
        _updateModel(modelName, val) {
            // Similar to the `stickitChange` object, include a `editBehaviorChange` exposing the associated view.
            const editBehaviorChange = {
                view: this.view
            };

            this._model.set(modelName, val, {editBehaviorChange});
        },

        /**
         * @private
         * Factory method that returns a new default "save" function for an edit field backed by a model, which will
         * save a model once its "name" property has been updated, returning the promise for the actual sync request.
         *
         * @param {String} name    The model property to set before saving; never falsey.
         * @param {String} [model] The view property storing the model to update, defaulting to "model".
         * @return {Function} A new "save" function for the edit field in question; never null. Will be invoked
         *                    with the view as scope.
         *
         * @see    #_cancel
         */
        _save(name, model) {
            return (val) => {
                model.set(name, val);

                return model.saveLater();
            };
        },

        /**
         * @private
         * Factory method that returns a new default "cancel" method for any edit field backed by a model.
         *
         * @param {String} [model] The view property storing the model to update, defaulting to "model".
         * @return {Function} A new "cancel" function for the edit field in question; never null. Will be invoked
         *                    with the view as scope.
         */
        _cancel(model) {
            return () => {
                return SyncManager.cancel(this[model || "model"], "save");
            };
        },

        /**
         *  onShow event from the view. It is needed a _defer function in this case because the StickIt will bind the elements
         *  after the onRender event. It is necessary to retrieve the originalValue.
         */
        onRender() {
            // Checks if the view isn't editable; if no such prop is specified, either as boolean or privilege, we
            // assume the CREATE privilege is required...
            let editable = this.view.options.isEditable;

            if (_.isUndefined(editable)) {
                editable = Permissions.Privileges.CREATE;
            }

            // If a privilege, check to see if editable...
            let privilege = Permissions.Privileges.of(editable);

            if (privilege) {
                editable = privilege.can();
            }

            _.defer(() => {
                // Bind hover events on the view
                this._bindEventsView();

                this.fields = [];

                // Resolve the data/view model to update - defaults to this.view.model...
                const viewModel = () => {
                    const model = this.options.viewModel;

                    if (model) {
                        if (_.isFunction(model)) {
                            return model.call(this.view);
                        }

                        return model;
                    }

                    return this.view.model;
                };

                this._model = viewModel();

                const editFields = this.options.editFields;

                _.every(editFields, (editField) => {
                    let save = editField.save;
                    let cancel = editField.cancel;
                    const model = editField.model;
                    const tooltip = _.isUndefined(editField.tooltip) || editField.tooltip;
                    const hideIcon = !_.isUndefined(editField.hideIcon) || editField.hideIcon;
                    const enableFieldEvents = _.isUndefined(editField.enableFieldEvents) || editField.enableFieldEvents;
                    const $editableField = this.view.$el.find(editField.input);

                    // Editable element not found, for instance in the Editor
                    if (!$editableField.length) {
                        return false;    // Break, since also the rest of elements wont have an editable field.
                    }

                    if (_.isString(save)) {
                        save = this._save(save, this._model);

                        if (cancel) {
                            const oldCancel = cancel;
                            const newCancel = this._cancel(this._model);

                            cancel = (val, originalVal) => {
                                newCancel();

                                oldCancel(val, originalVal);
                            };
                        } else {
                            cancel = this._cancel(this._model);
                        }
                    }

                    const field = this.createFieldObject(
                        $editableField,
                        editField.icon ? this.view.$el.find(editField.icon) : null,
                        editField.hideOnFocus,
                        model,
                        save,
                        cancel,
                        tooltip,
                        hideIcon
                    );

                    if (editable) {
                        this.fields.push(field);

                        this._addImageIcon(field);

                        // Reason for this is because we want to disable events when for instance clicking on top
                        // of the edit name field.
                        if (enableFieldEvents) {
                            this._bindEventsField(field);
                        }
                    }

                    if (tooltip) {
                        this.updateTooltip(field);
                    }

                    // If the behavior is created with "focused" set to
                    // true, then we focus on the input element.
                    if (editField.focused) {
                        this.getViewContainer().trigger("mousedown");
                    }
                });
            });
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/behaviors/EditBehavior.js